/**
 * Der Verlauf eines Datenpunkts als Diagramm (HM-053, seit HM-054 auf dem
 * gemeinsamen Diagramm).
 *
 * **Diese Datei zeichnet nichts mehr.** Sie übersetzt die Antwort des Servers
 * in den Vertrag von `@hausmeter/ui/chart` — Reihen, Lücken, Spanne, Marken —
 * und hört da auf. Bis HM-054 lag hier ein selbstgezeichnetes SVG samt eigener
 * Geometrie; das war für **eine** Ansicht richtig und wäre für die zweite der
 * Anfang zweier Diagrammwelten gewesen.
 *
 * Drei Übersetzungen stecken darin, und alle drei sind fachlich:
 *
 * **Das Band gehört zum Mittel.** Bei einem Zählerstand am Abschnittsende sagen
 * `min` und `max` nichts, was die Linie nicht schon zeigt; bei einem gemittelten
 * Momentanwert sind sie die Spitze, die das Mittel verschluckt hat.
 *
 * **Die Rasterweite kommt aus der Antwort und wird nicht geschätzt.** Sie
 * entscheidet, ab wann zwei Punkte eine Lücke zwischen sich haben — und der
 * Server weiß es genau (`resolution`).
 *
 * **Ein leerer Abschnitt ist noch keine Lücke** (#99). Ein Wärmemengenzähler wird
 * alle paar Minuten bis Stunden gelesen; im Raster liegen zwischen zwei Werten
 * leere Abschnitte. Der Server nennt seinen Takt (`readingIntervalSeconds`), und
 * das Diagramm bricht die Linie erst bei einer ausgebliebenen Auslesung ab.
 *
 * **Eine gelöschte Zeit ist keine Lücke.** Vor `retainedFrom` liegen keine
 * Rohwerte mehr vor, weil sie planmäßig gelöscht sind (HM-048). Diese Zeit
 * bekommt eine Schraffur; ohne sie läse man dort einen tagelangen Ausfall.
 */
import { Chart, type ChartAnnotation, type ChartSeries } from "@hausmeter/ui/chart";
import type { ApiComponents } from "@hausmeter/sdk";
import type { DatapointFormatter } from "../lib/datapoint-format.js";

type History = ApiComponents["schemas"]["DatapointHistoryResponse"];
type Datapoint = ApiComponents["schemas"]["Datapoint"];

export function HistoryChart({
  history,
  point,
  formatter,
  timeZone,
}: {
  readonly history: History;
  readonly point: Datapoint;
  /** Zahl und Einheit — eine Größenordnung für die ganze Reihe. */
  readonly formatter: DatapointFormatter;
  /** Die Zeitzone der Anlage, nie die des Browsers. */
  readonly timeZone: string;
}) {
  const series: ChartSeries = {
    id: point.id,
    label: point.name,
    // Fläche und Linie: Die Fläche gibt der Kurve Gewicht, ohne eine zweite
    // Aussage zu machen — sie endet an derselben Stelle wie die Linie.
    mark: "area",
    band: history.aggregate === "mean",
    points: history.points.map((entry) => ({
      at: new Date(entry.t).getTime(),
      value: entry.v,
      min: entry.min,
      max: entry.max,
    })),
  };

  const from = new Date(history.from).getTime();
  const retained = history.retainedFrom === null ? null : new Date(history.retainedFrom).getTime();
  const annotations: ChartAnnotation[] =
    retained !== null && retained > from
      ? [{ at: from, until: retained, label: "nicht mehr aufbewahrt" }]
      : [];

  return (
    <Chart
      series={[series]}
      from={from}
      to={new Date(history.to).getTime()}
      bucket={history.resolution}
      cadence={history.readingIntervalSeconds === null ? undefined : history.readingIntervalSeconds * 1000}
      format={formatter}
      timeZone={timeZone}
      height={210}
      annotations={annotations}
      label={`Verlauf von ${point.name}, ${history.points.length} Werte`}
    />
  );
}
